import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Box, CircularProgress } from '@mui/material';

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { hasPermission, isLoading: isPermissionsLoading, userPermissions } = usePermissions();
  const location = useLocation();

  // Memoize the loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() => {
    return isAuthLoading || isPermissionsLoading;
  }, [isAuthLoading, isPermissionsLoading]);

  // Memoize the user permissions length to prevent unnecessary re-renders
  const userPermissionsLength = useMemo(() => {
    return userPermissions?.length || 0;
  }, [userPermissions]);

  // Memoize the location pathname to prevent unnecessary re-renders
  const memoizedPathname = useMemo(() => location.pathname, [location.pathname]);

  // Memoize the permissions check to prevent unnecessary re-renders
  const permissionsCheck = useMemo(() => {
    if (!requiredPermission) return { shouldRender: true };
    
    const [service, resource, action] = requiredPermission.split(':');
    
    if (!userPermissions || userPermissions.length === 0) {
      return { shouldRender: false, reason: 'loading' };
    }
    
    if (!hasPermission(service, resource, action)) {
      return { shouldRender: false, reason: 'no-permission' };
    }
    
    return { shouldRender: true };
  }, [requiredPermission, userPermissions, hasPermission]);


  // Memoize the loading spinner to prevent unnecessary re-renders
  const loadingSpinner = useMemo(() => (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100px"
      sx={{ 
        opacity: 0.7,
        transition: 'opacity 0.3s ease-in-out'
      }}
    >
      <CircularProgress size={24} />
    </Box>
  ), []);

  // Show loading spinner only when authentication or permissions are loading
  if (isLoading) {
    return loadingSpinner;
  }

  // If user is not authenticated, redirect to login page
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Handle permissions check
  if (permissionsCheck.reason === 'loading') {
    return loadingSpinner;
  }

  if (permissionsCheck.reason === 'no-permission') {
    return <Navigate to="/" replace />;
  }

  // If everything is fine, render the requested component
  return children;
};

export default ProtectedRoute;
